"use client";

import { getCookie } from "cookies-next";
import { ReactNode, useMemo, createContext, useContext } from "react";
import { ulid } from "ulid";
import { storage } from "~/components/utils/storage";
import { create as createGraphql } from "../graphql-client";
import { AppAPIRestClient } from "./rest";

export interface ApiConfig {
  name?: string;
  apiUrl: string;
  apiKey: string;
  appleWebClientId?: string;
}

const restClientContext = createContext(new AppAPIRestClient("", ""));

const graphqlContext = createContext(
  createGraphql({ apiKey: "", apiUrl: "" }, () => ""),
);

export function APIClientProvider(props: {
  children: ReactNode;
  apiConfig: ApiConfig;
}) {
  const { apiConfig } = props;

  const { apiUrl, apiKey } = apiConfig;

  const restClient = useMemo(
    () => new AppAPIRestClient(apiUrl, apiKey),
    [apiUrl, apiKey],
  );

  const graphql = createGraphql(apiConfig, () => getCookie("token") as string);

  return (
    <restClientContext.Provider value={restClient}>
      <graphqlContext.Provider value={graphql}>
        {props.children}
      </graphqlContext.Provider>
    </restClientContext.Provider>
  );
}

export function useApiClient() {
  return useContext(restClientContext);
}

export function useGraphql() {
  return useContext(graphqlContext);
}

/**
 * Used to identify the user agent, this can easily be reset by the user
 * so it doesn't provide security in that regard, however it is hard to guess
 * someone elses deviceId, use to identify returning users. Also can pin feature
 * flags to a device id
 */
export function getDeviceId(): string {
  if (typeof window === "undefined")
    throw new Error("device can not be used server side");

  const ls = storage();

  let deviceId = ls.getItem("device_id");

  if (!deviceId) {
    deviceId = ulid();
    ls.setItem("device_id", deviceId);
  }

  return deviceId;
}
